import { useState, useEffect } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

interface Referral {
  id: number;
  caseNumber: string;
  childName: string;
  agency: string;
  purpose: string;
  status: 'pending' | 'accepted' | 'completed' | 'rejected';
  date: string;
  officer?: string;
  notes?: string;
  priority: 'standard' | 'urgent' | 'emergency';
}

const statusConfig = {
  pending: { color: 'bg-amber-100 text-amber-700', icon: 'ri-time-line' },
  accepted: { color: 'bg-teal-100 text-teal-700', icon: 'ri-checkbox-circle-line' },
  completed: { color: 'bg-green-100 text-green-700', icon: 'ri-check-double-line' },
  rejected: { color: 'bg-red-100 text-red-700', icon: 'ri-close-circle-line' },
};

const priorityConfig = {
  standard: 'bg-gray-100 text-gray-600',
  urgent: 'bg-orange-100 text-orange-700',
  emergency: 'bg-red-100 text-red-700',
};

export default function ReferralsPage() {
  const { user } = useAuth();
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedRef, setSelectedRef] = useState<Referral | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [form, setForm] = useState({ caseNumber: '', childName: '', agency: '', purpose: '', priority: 'standard' as Referral['priority'], officer: '', notes: '' });
  const [children, setChildren] = useState<any[]>([]);
  
  const filtered = referrals.filter((r) => filterStatus === 'all' || r.status === filterStatus);

  const updateStatus = async (id: number, status: Referral['status']) => {
    await fetch("https://guardiancloudcare.com/backend/api/update_referral_status.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id,
        status,
        uid: user?.uid
      })
    });

    fetchReferrals();
  };

  const fetchReferrals = () => {
    fetch(`https://guardiancloudcare.com/backend/api/get_referrals.php?uid=${user?.uid}`)
      .then(res => res.json())
      .then(data => {
        const mapped = data.map((r: any) => ({
          id: r.id,
          caseNumber: r.case_number,
          childName: r.child_name,
          agency: r.agency,
          purpose: r.purpose,
          status: r.status || 'pending',
          priority: r.priority || 'standard',
          officer: r.officer || '',
          notes: r.notes || '',

          date: new Date(r.created_at).toLocaleString(),
        }));

        setReferrals(mapped);
      })
      .catch(err => console.error("Fetch referrals error:", err));
  };

  useEffect(() => {
    fetchReferrals();

    fetch(`https://guardiancloudcare.com/backend/api/get_children.php?uid=${user?.uid}`)
      .then(res => res.json())
      .then(data => setChildren(data));
  }, []);

  const handleAdd = async () => {
    if (!form.caseNumber || !form.agency || !form.purpose) return;

    await fetch("https://guardiancloudcare.com/backend/api/add_referral.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...form,
        uid: user?.uid
      })
    });

    fetchReferrals(); 

    setShowModal(false);

    setForm({
      caseNumber: '',
      childName: '',
      agency: '',
      purpose: '',
      priority: 'standard',
      officer: '',
      notes: ''
    });
  };


  return (
    <AppLayout title="Referral Management" breadcrumb={['Home', 'Referral Management']}>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {(['pending', 'accepted', 'completed', 'rejected'] as Referral['status'][]).map((s) => {
          const count = referrals.filter((r) => r.status === s).length;
          const cfg = statusConfig[s];
          return (
            <div key={s} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="flex items-center justify-between mb-2">
                <i className={`${cfg.icon} text-lg ${cfg.color.split(' ')[1]}`} />
                <span className="text-2xl font-bold text-gray-900">{count}</span>
              </div>
              <p className="text-sm text-gray-500 capitalize">{s}</p>
            </div>
          );
        })}
      </div>

      {/* Filters + Actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'accepted', 'completed', 'rejected'].map((f) => (
            <button key={f} onClick={() => setFilterStatus(f)}
              className={`text-sm px-4 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap capitalize
                ${filterStatus === f ? 'bg-teal-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {f}
            </button>
          ))}
        </div>
        <button onClick={() => setShowModal(true)} className="bg-teal-500 text-white text-sm px-5 py-2 rounded-xl hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
          <i className="ri-add-line" />New Referral
        </button>
      </div>

      {/* Referrals Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-5 py-3 text-left font-medium">Case / Child</th>
                <th className="px-5 py-3 text-left font-medium">Referring Agency</th>
                <th className="px-5 py-3 text-left font-medium hidden md:table-cell">Purpose</th>
                <th className="px-5 py-3 text-left font-medium">Priority</th>
                <th className="px-5 py-3 text-left font-medium">Status</th>
                <th className="px-5 py-3 text-left font-medium hidden lg:table-cell">Date</th>
                <th className="px-5 py-3 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-gray-400">
                    No referrals found
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3">
                    <p className="text-sm font-medium text-gray-800">{r.childName}</p>
                    <p className="text-xs font-mono text-gray-400">{r.caseNumber}</p>
                  </td>
                  <td className="px-5 py-3">
                    <p className="text-sm text-gray-700">{r.agency}</p>
                    {r.officer && <p className="text-xs text-gray-400">{r.officer}</p>}
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell">
                    <p className="text-xs text-gray-600 max-w-48 truncate">{r.purpose}</p>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${priorityConfig[r.priority]}`}>{r.priority}</span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${statusConfig[r.status].color}`}>{r.status}</span>
                  </td>
                  <td className="px-5 py-3 hidden lg:table-cell text-xs text-gray-500">{r.date}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => { setSelectedRef(r); setShowDetailModal(true); }} className="text-xs bg-gray-100 text-gray-700 px-2 py-1.5 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap">View</button>
                      {r.status === 'pending' && (
                        <>
                          <button onClick={() => updateStatus(r.id, 'accepted')} className="text-xs bg-teal-100 text-teal-700 px-2 py-1.5 rounded-lg hover:bg-teal-200 cursor-pointer whitespace-nowrap">Accept</button>
                          <button onClick={() => updateStatus(r.id, 'rejected')} className="text-xs bg-red-50 text-red-600 px-2 py-1.5 rounded-lg hover:bg-red-100 cursor-pointer whitespace-nowrap">Reject</button>
                        </>
                      )}
                      {r.status === 'accepted' && (
                        <button onClick={() => updateStatus(r.id, 'completed')} className="text-xs bg-green-100 text-green-700 px-2 py-1.5 rounded-lg hover:bg-green-200 cursor-pointer whitespace-nowrap">Complete</button>
                      )}
                    </div>
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Referral Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">Create New Referral</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="space-y-4">
              <select
                value={form.caseNumber}
                onChange={(e) => {
                  const selected = children.find(c => c.case_number === e.target.value);
                  setForm({
                    ...form,
                    caseNumber: selected?.case_number || '',
                    childName: selected?.full_name || ''
                  });
                }}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm"
              >
                <option value="">Select Child</option>
                {children.map((c) => (
                  <option key={c.case_number} value={c.case_number}>
                    {c.full_name} ({c.case_number})
                  </option>
                ))}
              </select>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Referring Agency <span className="text-red-500">*</span></label>
                <input value={form.agency} onChange={(e) => setForm({ ...form, agency: e.target.value })} placeholder="Agency name" className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Purpose <span className="text-red-500">*</span></label>
                <textarea value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} rows={2} maxLength={500} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Priority</label>
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value as Referral['priority'] })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 cursor-pointer">
                  <option value="standard">Standard</option>
                  <option value="urgent">Urgent</option>
                  <option value="emergency">Emergency</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} maxLength={500} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 resize-none" />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-5">
              <button onClick={() => setShowModal(false)} className="text-sm border border-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50">Cancel</button>
              <button onClick={handleAdd} className="text-sm bg-teal-500 text-white px-5 py-2 rounded-xl hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap">Create Referral</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRef && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-900">Referral Details</h3>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="space-y-3 text-sm">
              {[
                ['Case Number', selectedRef.caseNumber],
                ['Child Name', selectedRef.childName],
                ['Agency', selectedRef.agency],
                ['Officer', selectedRef.officer || 'Not assigned'],
                ['Purpose', selectedRef.purpose],
                ['Priority', selectedRef.priority],
                ['Status', selectedRef.status],
                ['Date', selectedRef.date],
                ['Notes', selectedRef.notes || 'None'],
              ].map(([label, value]) => (
                <div key={label} className="flex gap-3">
                  <span className="text-gray-400 w-28 flex-shrink-0">{label}:</span>
                  <span className="font-medium text-gray-800 capitalize">{value}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-5">
              <button onClick={() => setShowDetailModal(false)} className="text-sm bg-teal-500 text-white px-5 py-2 rounded-xl hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap">Close</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
